// Reconciles credential absence on every client route before Sanctum's middleware.
import { useLogger } from '~/composables/useLogger'
import { sanctumTokenStorage } from '~/utils/sanctumTokenStorage'
import { resetAccountScopedState } from '~/composables/resetAccountScopedState'
import { isHydratingServerRender } from '~/utils/isHydratingServerRender'
import { pendingIdentityLoads } from '~/utils/pendingIdentityLoads'
import { refreshCurrentIdentity } from '~/utils/refreshCurrentIdentity'
import { refreshStoredIdentity } from '~/utils/refreshStoredIdentity'

const logger = useLogger('IdentityReconciliationMiddleware')

const readStoredToken = (path: string): string | undefined => {
  try {
    return sanctumTokenStorage.read()
  } catch (error: unknown) {
    logger.warn('staleIdentityStorageReadFailed', { path, error })
    return undefined
  }
}

export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) return

  const nuxtApp = useNuxtApp()
  const user = useSanctumUser()
  const userStore = useUserStore()
  const notificationsStore = useNotificationsStore()

  const clearStaleIdentity = async (cause: string): Promise<void> => {
    const result = await resetAccountScopedState(nuxtApp, { clearToken: token === undefined })
    logger.warn('staleIdentityCleared', { path: to.path, cause })
    if (result.cleared) return
    logger.error('staleIdentityClearIncomplete', {
      path: to.path,
      cause,
      failedSteps: result.failedSteps
    })
  }

  let token = readStoredToken(to.path)
  if (to.meta.sanctum?.excluded !== true && token !== undefined) return

  const pending = pendingIdentityLoads.get(nuxtApp)
  if (token !== undefined && pending !== undefined) {
    await pending.catch(() => undefined)
    token = readStoredToken(to.path)
  }

  // The vendor's 401 handler only nulls the Sanctum ref -- it never touches these
  // stores, so a null ref with initialized/loaded stores is itself stale state.
  if (user.value === null) {
    if (userStore.initialized || notificationsStore.isLoaded) {
      await clearStaleIdentity('partialClear')
      return
    }
    if (token === undefined) return
    if (isHydratingServerRender(nuxtApp)) return
    try {
      await refreshStoredIdentity(nuxtApp, async () => {
        if (user.value !== null) return
        await refreshCurrentIdentity(nuxtApp, logger, { initialize: true })
      })
    } catch (error: unknown) {
      logger.warn('storedCredentialUnverified', { path: to.path, error })
    }
    return
  }

  if (token !== undefined) return

  user.value = null
  await clearStaleIdentity('tokenMissing')
})
